import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="privacy-page">
      <header className="privacy-page-header">
        <div className="privacy-header-content">
          <button className="back-button" onClick={handleBack}>
            <MdArrowBack />
          </button>
          <div className="privacy-title-section">
            <img src="/icon.png" alt="$Ditto" className="privacy-header-icon" />
            <h1>$Ditto — Privacy Policy</h1>
          </div>
        </div>
      </header>

      <div className="privacy-page-content">
        <div className="privacy-container">
          <p className="privacy-intro">
            <strong>Effective Date:</strong> December 4, 2025
          </p>

          <p className="privacy-intro">
            Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal and financial information when you use our personal finance application built on AWS cloud infrastructure.
          </p>

          <h2>1. Information We Collect</h2>
          <p>We collect the following types of information:</p>
          <ul>
            <li><strong>Account Information:</strong> When you sign up using Google OAuth, we collect your name, email address, and profile picture from your Google account.</li>
            <li><strong>Financial Data:</strong> Through Stripe Financial Connections, we access your bank account information, transaction history, and account balances to provide our services.</li>
            <li><strong>Transaction Data:</strong> We store and process your transaction information including amounts, dates, categories, and descriptions.</li>
            <li><strong>Usage Data:</strong> We may collect information about how you use the application, including features accessed and preferences set.</li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and improve our personal finance management services</li>
            <li>Sync and categorize your financial transactions</li>
            <li>Create budgets and spending insights</li>
            <li>Send you important updates about your account</li>
            <li>Ensure the security and integrity of our services</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>

          <h2>3. Data Storage and Infrastructure</h2>
          <p>Your data is stored securely using Amazon Web Services (AWS) cloud infrastructure:</p>
          <ul>
            <li><strong>AWS DynamoDB:</strong> Your financial data is stored in AWS DynamoDB, a fully managed NoSQL database service with enterprise-grade security and encryption</li>
            <li><strong>AWS Lambda:</strong> Our backend runs on AWS Lambda, providing isolated, secure serverless compute environments with no permanent data storage</li>
            <li><strong>Data Residency:</strong> Your data is stored in AWS data centers in the United States (or your specified region)</li>
            <li><strong>AWS Compliance:</strong> AWS maintains certifications including SOC 2, ISO 27001, PCI DSS, and GDPR compliance</li>
          </ul>

          <h2>4. Data Sharing and Third Parties</h2>
          <p>We work with the following third-party services:</p>
          <ul>
            <li><strong>Google OAuth:</strong> For secure authentication (only receives your name, email, and profile picture)</li>
            <li><strong>Stripe Financial Connections:</strong> For secure bank account connectivity and transaction syncing (read-only access, PCI DSS Level 1 compliant)</li>
            <li><strong>Amazon Web Services (AWS):</strong> For secure data storage and processing infrastructure</li>
          </ul>
          <p>We do not sell your personal or financial information to third parties. We only share data with these service providers as necessary to operate our application, and they are bound by strict confidentiality and security requirements.</p>

          <h2>5. Data Security</h2>
          <p>We implement industry-leading security measures to protect your data:</p>
          <ul>
            <li><strong>Encryption in Transit:</strong> All data is transmitted using secure HTTPS/TLS 1.3 connections between your browser and our servers</li>
            <li><strong>Encryption at Rest:</strong> Your data is encrypted in AWS DynamoDB using AWS Key Management Service (KMS) with 256-bit AES encryption</li>
            <li><strong>Financial Data Protection:</strong> Bank account data is accessed through Stripe's secure Financial Connections API</li>
            <li><strong>Authentication:</strong> We use industry-standard JWT (JSON Web Token) authentication with secure, encrypted secret keys</li>
            <li><strong>Access Control:</strong> Strict data isolation ensures each user can only access their own financial information through partition key design</li>
            <li><strong>Serverless Architecture:</strong> AWS Lambda functions run in isolated execution environments with least-privilege IAM roles</li>
            <li><strong>Monitoring:</strong> Continuous security monitoring and audit logging via AWS CloudWatch and CloudTrail</li>
            <li><strong>No Session Storage:</strong> Stateless authentication eliminates traditional session hijacking risks</li>
            <li><strong>Input Validation:</strong> All user inputs are validated and sanitized to prevent injection attacks</li>
          </ul>

          <h2>6. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access:</strong> View and download all your personal and financial data</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your account and all associated data (Right to Erasure under GDPR)</li>
            <li><strong>Portability:</strong> Export your transaction data in a machine-readable format</li>
            <li><strong>Revoke Access:</strong> Disconnect your bank accounts at any time through Stripe</li>
            <li><strong>Object:</strong> Object to processing of your data for specific purposes</li>
            <li><strong>Restrict:</strong> Request restriction of processing under certain circumstances</li>
          </ul>
          <p>To exercise these rights, please contact us or use the account management features in your dashboard.</p>

          <h2>7. Data Retention and Backups</h2>
          <p>We retain your data for as long as your account is active. Our data retention practices include:</p>
          <ul>
            <li><strong>Active Accounts:</strong> Data retained indefinitely while your account is active</li>
            <li><strong>Backups:</strong> AWS Point-in-Time Recovery maintains automated backups for disaster recovery (35 days rolling window)</li>
            <li><strong>Account Deletion:</strong> When you delete your account, all your personal and financial data is permanently removed from our production systems within 30 days</li>
            <li><strong>Legal Compliance:</strong> Some data may be retained longer if required by law or for legitimate business purposes (e.g., fraud prevention, regulatory compliance)</li>
            <li><strong>Audit Logs:</strong> Security and access logs are retained for 90 days for security monitoring and incident investigation</li>
          </ul>

          <h2>8. International Data Transfers</h2>
          <p>If you are accessing our service from outside the United States:</p>
          <ul>
            <li>Your data may be transferred to and stored in AWS data centers in the United States or other regions</li>
            <li>We ensure appropriate safeguards are in place for international data transfers in compliance with GDPR</li>
            <li>AWS complies with applicable data protection frameworks for international transfers</li>
            <li>You may request your data be stored in a specific AWS region (EU, US, etc.) by contacting us</li>
          </ul>

          <h2>9. Security Incident Response</h2>
          <p>In the event of a data breach or security incident:</p>
          <ul>
            <li>We will notify affected users within 72 hours as required by GDPR and applicable regulations</li>
            <li>We will provide details about the nature of the breach, data affected, and steps taken to address it</li>
            <li>We maintain comprehensive security logs via AWS CloudTrail for incident investigation</li>
            <li>We have automated monitoring and alert systems to detect and respond to security threats in real-time</li>
          </ul>

          <h2>10. Cookies and Tracking</h2>
          <p>Our application uses minimal cookies and tracking:</p>
          <ul>
            <li><strong>Authentication Token:</strong> Stored in browser localStorage (not cookies) for user session management</li>
            <li><strong>No Third-Party Trackers:</strong> We do not use advertising cookies or third-party analytics trackers</li>
            <li><strong>Essential Only:</strong> Only technically necessary data storage for application functionality</li>
          </ul>

          <h2>11. Children's Privacy</h2>
          <p>Our service is not intended for users under the age of 18. We do not knowingly collect information from children under 18. If we discover we have collected personal information from a child, we will promptly delete it.</p>

          <h2>12. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, regulatory, or operational reasons. We will:</p>
          <ul>
            <li>Notify you of significant changes via email or prominent notice in the application</li>
            <li>Update the "Effective Date" at the top of this policy</li>
            <li>Obtain your consent for material changes that affect how we use your data</li>
          </ul>
          <p>Continued use of the service after changes constitutes acceptance of the updated policy.</p>

          <h2>13. Contact Us</h2>
          <p>If you have any questions or concerns about this Privacy Policy or our data practices:</p>
          <ul>
            <li><strong>Privacy Questions:</strong> privacy@your-domain.com</li>
            <li><strong>Security Issues:</strong> security@your-domain.com</li>
            <li><strong>Data Requests:</strong> Use the "Export Data" or "Delete Account" features in your account settings</li>
          </ul>

          <h2>14. Additional Information</h2>
          <p>For more technical details about our security practices, please refer to:</p>
          <ul>
            <li><a href="https://github.com/your-username/finance-app/blob/main/AWS_SECURITY.md" target="_blank" rel="noopener noreferrer">Technical Security Documentation</a></li>
            <li><a href="https://aws.amazon.com/compliance/" target="_blank" rel="noopener noreferrer">AWS Compliance Programs</a></li>
            <li><a href="https://stripe.com/docs/security" target="_blank" rel="noopener noreferrer">Stripe Security Practices</a></li>
          </ul>

          <div className="privacy-callout">
            <p className="privacy-callout-title">Your Data, Your Control</p>
            <p>We are committed to transparency and giving you full control over your data. You can export or delete your data at any time from your account settings.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
